import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Divider,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  Check,
  Close,
  ExpandMore,
  HelpOutline,
  Money,
  BarChart,
  Security,
  Key,
  SupportAgent,
  VerifiedUser,
  Article,
  Refresh
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import pricingService from "../services/pricingService";
import axios from 'axios';

export default function Pricing() {
  const [expanded, setExpanded] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchPricingTiers = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      setIsRefreshing(forceRefresh);
      console.log("🔍 Attempting to fetch pricing tiers...", forceRefresh ? "(forced refresh)" : "");
      
      // Try our service first
      try {
        const tiers = await pricingService.getPricingTiers(forceRefresh);
        console.log("✅ Successfully fetched pricing tiers:", tiers);
        
        if (Array.isArray(tiers) && tiers.length > 0) {
          // Sort by price (lowest to highest)
          const sortedTiers = tiers.sort((a, b) => a.basePrice - b.basePrice);
          setPricingTiers(sortedTiers);
          return; // Exit if successful
        } else {
          console.warn("❌ Retrieved pricing tiers not in expected format:", tiers);
          throw new Error("Invalid pricing tiers data format");
        }
      } catch (serviceError) {
        console.error("❌ Error using pricingService:", serviceError);
        
        // Try direct API call as fallback with cache busting
        try {
          console.log("🔄 Trying direct API call as fallback...");
          const timestamp = Date.now();
          const response = await axios.get(`/api/pricing-tiers?_=${timestamp}`);
          console.log("📡 Direct API response:", response);
          
          if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
            const sortedTiers = response.data.data.sort((a, b) => a.basePrice - b.basePrice);
            setPricingTiers(sortedTiers);
            return; // Exit if successful
          } else {
            throw new Error("Invalid response from API");
          }
        } catch (directError) {
          console.error("❌ Direct API call also failed:", directError);
          throw new Error("Could not retrieve pricing data from any source");
        }
      }
    } catch (error) {
      console.error('❌ Could not retrieve pricing data:', error);
      setError("Pricing information is currently unavailable. Please try again later or contact support.");
      setPricingTiers([]);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };
  
  useEffect(() => {
    fetchPricingTiers();
  }, []);
  
  const handleRefresh = () => {
    fetchPricingTiers(true);
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Helper function to format currency
  const formatCurrency = (cents) => {
    if (cents === 0) return '€0';
    return pricingService.formatCurrency(cents, { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    });
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  console.log("Rendering pricing tiers:", pricingTiers);

  return (
    <Container>
      <Box
        className="inner-content"
        sx={{
          py: 5,
        }}
      >
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 4 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRefresh} 
                startIcon={<Refresh />}
                disabled={isRefreshing}
              >
                {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        
        {/* Hero Section */}
        <Box sx={{ 
          textAlign: "center", 
          mb: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center' 
        }}>
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", md: "3rem" }
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2, maxWidth: 700, mx: "auto" }}>
            Choose the plan that fits your EUDR compliance needs
          </Typography>
        </Box>

        {/* Pricing Tiers */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            mb: 8,
            justifyContent: "center"
          }}
        >
          {Array.isArray(pricingTiers) && pricingTiers.length > 0 ? (
            pricingTiers.map((tier) => {
              const isProfessional = tier.name === 'professional';
              
              return (
                <Card 
                  key={tier.name}
                  sx={{ 
                    width: { xs: "100%", md: isProfessional ? 350 : 330 }, 
                    display: "flex", 
                    flexDirection: "column",
                    ...(isProfessional && {
                      border: 2, 
                      borderColor: "primary.main",
                      transform: { md: "scale(1.05)" },
                      zIndex: 1,
                      boxShadow: 10
                    })
                  }}
                >
                  <CardHeader
                    title={tier.displayName}
                    subheader={tier.description}
                    sx={{ 
                      bgcolor: isProfessional ? "primary.main" : "background.paper", 
                      color: isProfessional ? "white" : "inherit", 
                      textAlign: "center", 
                      pb: 1 
                    }} 
                    titleTypographyProps={{ 
                      variant: "h5", 
                      fontWeight: "bold",
                      color: isProfessional ? "inherit" : undefined
                    }}
                    subheaderTypographyProps={{
                      color: isProfessional ? "white" : undefined
                    }}
                  />
                  <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, pt: 0 }}>
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="h3" fontWeight="bold" color="primary">
                        {formatCurrency(tier.basePrice)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per month
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <List sx={{ flexGrow: 1 }}>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Check color="success" />
                        </ListItemIcon>
                        <ListItemText primary={`${tier.monthlyLimit?.toLocaleString() || 0} API calls/month`} />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Check color="success" />
                        </ListItemIcon>
                        <ListItemText primary={`Up to ${tier.apiKeyLimit || 1} API ${(tier.apiKeyLimit || 1) === 1 ? 'Key' : 'Keys'}`} />
                      </ListItem>
                      {tier.features && tier.features.map((feature, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Check color="success" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                    <Link to="/register" style={{ textDecoration: "none", marginTop: 2 }}>
                      <Button 
                        variant={isProfessional ? "contained" : "outlined"} 
                        fullWidth 
                        size="large" 
                        sx={{ mt: 2 }}
                      >
                        {tier.name === 'free' ? 'Start For Free' : 'Get Started'}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            // Fallback if no pricing tiers are available
            <Box sx={{ textAlign: "center", width: "100%", py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Pricing information is currently unavailable.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please check back later or contact our support team.
              </Typography>
              <Button 
                component={Link} 
                to="/contact" 
                variant="outlined" 
                sx={{ mt: 2 }}
              >
                Contact Us
              </Button>
            </Box>
          )}
        </Box>

        {/* Usage-based Pricing */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}>
            Usage-Based Pricing
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, textAlign: "center" }}>
            Pay only for what you use beyond your monthly included API calls
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: "background.paper" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Service</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>Price per Call</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Article sx={{ mr: 1, color: "primary.main" }} />
                      DDS Submissions
                    </Box>
                  </TableCell>
                  <TableCell align="right">€0.20</TableCell>
                  <TableCell>Submit new DDS statements to the EUDR system</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Article sx={{ mr: 1, color: "primary.main" }} />
                      DDS Retrievals
                    </Box>
                  </TableCell>
                  <TableCell align="right">€0.08</TableCell>
                  <TableCell>Retrieve existing DDS statements</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Article sx={{ mr: 1, color: "primary.main" }} />
                      DDS Amendments
                    </Box>
                  </TableCell>
                  <TableCell align="right">€0.25</TableCell>
                  <TableCell>Modify existing DDS statements</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Article sx={{ mr: 1, color: "primary.main" }} />
                      DDS Retractions
                    </Box>
                  </TableCell>
                  <TableCell align="right">€0.25</TableCell>
                  <TableCell>Withdraw DDS statements from the EUDR system</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", textAlign: "center" }}>
            *Usage-based pricing applies to Professional and Enterprise plans only. Free plan is limited to 250 API calls per month.
          </Typography>
        </Box>

        {/* Add-ons */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}>
            Available Add-ons
          </Typography>
          
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: "100%", borderLeft: 3, borderColor: "primary.main" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Key sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Additional API Keys
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Separate access credentials for different teams or applications
                </Typography>
                <Chip label="€10/month per additional key" color="primary" variant="outlined" />
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: "100%", borderLeft: 3, borderColor: "primary.main" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BarChart sx={{ mr: 1, color: "primary.main" }} />
                  <Typography variant="h6" fontWeight="bold">
                    Basic Usage Reports
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Monthly PDF reports with detailed usage analytics
                </Typography>
                <Chip label="€20/month" color="primary" variant="outlined" />
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Feature Comparison */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}>
            Plan Comparison
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ bgcolor: "background.paper" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Feature</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Free</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Professional</TableCell>
                  <TableCell align="center" sx={{ fontWeight: "bold" }}>Enterprise</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>Monthly API Calls</TableCell>
                  <TableCell align="center">250</TableCell>
                  <TableCell align="center">5,000</TableCell>
                  <TableCell align="center">25,000</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>API Keys</TableCell>
                  <TableCell align="center">1</TableCell>
                  <TableCell align="center">Up to 3</TableCell>
                  <TableCell align="center">Up to 10</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>Support Response Time</TableCell>
                  <TableCell align="center">48 hours</TableCell>
                  <TableCell align="center">24 hours</TableCell>
                  <TableCell align="center">8 hours</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>Usage Analytics</TableCell>
                  <TableCell align="center"><Close color="error" /></TableCell>
                  <TableCell align="center">Basic</TableCell>
                  <TableCell align="center">Advanced</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>Monthly Reports</TableCell>
                  <TableCell align="center"><Close color="error" /></TableCell>
                  <TableCell align="center">Available as add-on</TableCell>
                  <TableCell align="center"><Check color="success" /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "medium" }}>Pay-as-you-go Pricing</TableCell>
                  <TableCell align="center"><Close color="error" /></TableCell>
                  <TableCell align="center"><Check color="success" /></TableCell>
                  <TableCell align="center"><Check color="success" /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* FAQs */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: "bold", textAlign: "center" }}>
            Frequently Asked Questions
          </Typography>
          
          <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">How does the usage-based pricing work?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Each plan includes a set number of API calls per month. Once you exceed that limit, additional calls are charged at the per-call rates shown in the usage pricing table. Charges are calculated at the end of each billing cycle based on your actual usage.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">Can I upgrade or downgrade my plan?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Yes, you can change your plan at any time. When upgrading, the new plan takes effect immediately. When downgrading, the change will take effect at the start of your next billing cycle.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">Do you offer annual billing?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Yes, we offer annual billing with a 10% discount compared to monthly billing. Please contact our sales team for more information.
              </Typography>
            </AccordionDetails>
          </Accordion>
          
          <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight="medium">What happens if I exceed my monthly API call limit?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>
                Your service will continue to work. Any calls beyond your plan's limit will be charged according to the usage-based pricing table. For the Free plan, API calls are limited to 250 per month, and additional calls are not available.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Call to Action */}
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 6,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
            Ready to simplify your EUDR compliance?
          </Typography>
          <Typography sx={{ mb: 4 }}>
            Get started today with a free account or contact our sales team for a custom quote
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/register" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ px: 4, py: 1.5 }}
              >
                Start Your Free Trial
              </Button>
            </Link>
            <Link to="/contact" style={{ textDecoration: "none" }}>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                sx={{ px: 4, py: 1.5 }}
              >
                Contact Sales
              </Button>
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}


